import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, ArrowLeft, CheckCircle, Upload, X, ImagePlus } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALING_MODALITIES = [
  "Doctor", "Acupuncturist", "Herbalist", "Reiki Practitioner",
  "Massage Therapist", "Yoga Therapist", "Meditation Guide",
  "Naturopathic Doctor", "Sound Healer", "Energy Healer",
  "Breathwork Facilitator", "Aromatherapist", "Shaman",
  "Faith Healer", "Midwife", "Doula", "Nutritionist",
  "Chiropractor", "Therapist/Counselor",
  "Somatic Practitioner", "Life Coach", "Ayurvedic Practitioner",
  "Craniosacral Therapist", "Hypnotherapist",
  "Traditional Chinese Medicine (TCM)", "Astrology",
  "Human Design", "Gene Keys", "Akashic Records Reader"
];

const ETHNICITY_OPTIONS = [
  "African American/Black", "Afro-Caribbean", "Afro-Latina/o",
  "East Asian", "South Asian", "Southeast Asian", "Pacific Islander",
  "Indigenous/Native American", "Hispanic/Latina/o", "Middle Eastern/North African",
  "Mixed/Multiracial", "Other", "Prefer not to say"
];

const JoinAsHealer = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [selectedModalities, setSelectedModalities] = useState([]);
  const [customModality, setCustomModality] = useState('');
  const [showCustomModality, setShowCustomModality] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    phone_number: '',
    email: '',
    ethnicity: '',
    neighborhood: '',
    address: '',
    website: '',
    instagram: '',
    facebook: '',
    twitter: '',
    linkedin: '',
    other_info: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleModality = (modality) => {
    setSelectedModalities(prev =>
      prev.includes(modality)
        ? prev.filter(m => m !== modality)
        : [...prev, modality]
    );
  };

  const handlePhotoSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    const totalFiles = photoFiles.length + newFiles.length;

    if (totalFiles > 10) {
      toast.error('Maximum 10 photos allowed');
      return;
    }

    for (const file of newFiles) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error(`${file.name} is not a supported image format`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return;
      }
    }

    setPhotoFiles(prev => [...prev, ...newFiles]);

    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreviews(prev => [...prev, { name: file.name, url: e.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedModalities.length === 0 && !customModality.trim()) {
      toast.error('Please select at least one healing modality');
      return;
    }

    setSubmitting(true);

    try {
      // Upload photos first if any
      let uploadedPaths = [];
      if (photoFiles.length > 0) {
        setUploading(true);
        const photoFormData = new FormData();
        photoFiles.forEach(file => photoFormData.append('files', file));

        const uploadResponse = await axios.post(`${API}/upload/application-photos`, photoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedPaths = uploadResponse.data.paths;
        setUploading(false);
      }

      // Submit application
      await axios.post(`${API}/healer-applications`, {
        ...formData,
        healing_modalities: selectedModalities,
        custom_modality: customModality.trim() || null,
        photos: uploadedPaths
      });

      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <nav className="bg-white/80 backdrop-blur-lg border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <Heart className="w-8 h-8" style={{color: '#8B4513'}} />
              <span className="text-2xl font-bold text-gray-900">Ourmend</span>
            </div>
          </div>
        </nav>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="success-heading">Application Submitted!</h1>
          <p className="text-lg text-gray-700 mb-8">
            Thank you for your interest in joining Ourmend. We've received your application and will review it shortly.
            You'll hear from us at {formData.email} within 3-5 business days.
          </p>
          <Button onClick={() => navigate('/')} className="bg-[#8B4513] hover:bg-[#6B3410]" data-testid="return-home-btn">
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Helmet>
        <title>Join As A Healer | Ourmend</title>
        <meta name="description" content="Join Ourmend's network of healers of color. Apply to be listed and connect with clients seeking cultural care." />
      </Helmet>

      <nav className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Heart className="w-8 h-8" style={{color: '#8B4513'}} />
            <span className="text-2xl font-bold text-gray-900">Ourmend</span>
          </div>
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Join As A Healer</h1>
          <p className="text-lg text-gray-700">
            Help us build a diverse community of healing professionals. Fill out the form below to apply.
          </p>
        </div>

        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} required data-testid="name-input" />
                </div>
                <div>
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input id="business_name" name="business_name" value={formData.business_name} onChange={handleChange} required data-testid="business-name-input" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required data-testid="email-input" />
                </div>
                <div>
                  <Label htmlFor="phone_number">Phone Number *</Label>
                  <Input id="phone_number" name="phone_number" type="tel" value={formData.phone_number} onChange={handleChange} required data-testid="phone-input" />
                </div>
              </div>

              {/* Ethnicity */}
              <div>
                <Label htmlFor="ethnicity">Ethnicity / Background *</Label>
                <select
                  id="ethnicity"
                  name="ethnicity"
                  value={formData.ethnicity}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="ethnicity-select"
                >
                  <option value="">Select your ethnicity/background</option>
                  {ETHNICITY_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Healing Modalities */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Healing Modalities *</h2>
              <p className="text-sm text-gray-600">Select all that apply</p>

              <div className="flex flex-wrap gap-2" data-testid="modality-options">
                {HEALING_MODALITIES.map(mod => (
                  <button
                    key={mod}
                    type="button"
                    onClick={() => toggleModality(mod)}
                    className={`px-4 py-2 rounded-full border text-sm transition-all ${
                      selectedModalities.includes(mod)
                        ? 'bg-[#8B4513] text-white border-[#8B4513] font-semibold'
                        : 'border-gray-300 text-gray-700 hover:border-[#8B4513] hover:text-[#8B4513]'
                    }`}
                    data-testid={`modality-${mod.replace(/[\s/]+/g, '-').toLowerCase()}`}
                  >
                    {mod}
                  </button>
                ))}
              </div>

              {selectedModalities.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedModalities.map(mod => (
                    <Badge key={mod} className="bg-amber-100 text-amber-800 border-amber-300">
                      {mod}
                      <button type="button" onClick={() => toggleModality(mod)} className="ml-1.5 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Custom modality */}
              <div>
                {!showCustomModality ? (
                  <button
                    type="button"
                    onClick={() => setShowCustomModality(true)}
                    className="text-sm text-[#8B4513] hover:underline font-medium"
                    data-testid="add-custom-modality-btn"
                  >
                    + Don't see your modality? Add it here
                  </button>
                ) : (
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label htmlFor="custom_modality">Custom Modality</Label>
                      <Input
                        id="custom_modality"
                        value={customModality}
                        onChange={(e) => setCustomModality(e.target.value)}
                        placeholder="Enter your healing modality"
                        data-testid="custom-modality-input"
                      />
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => { setShowCustomModality(false); setCustomModality(''); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Location</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="neighborhood">Neighborhood *</Label>
                  <Input id="neighborhood" name="neighborhood" value={formData.neighborhood} onChange={handleChange} required data-testid="neighborhood-input" />
                </div>
                <div>
                  <Label htmlFor="address">Full Address *</Label>
                  <Input id="address" name="address" value={formData.address} onChange={handleChange} required data-testid="address-input" />
                </div>
              </div>
            </div>

            {/* Photos */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Photos</h2>
              <p className="text-sm text-gray-600">Upload up to 10 photos of yourself, your practice, or your space (JPEG, PNG, or WebP, max 5MB each)</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {photoPreviews.map((preview, index) => (
                  <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-amber-200">
                    <img src={preview.url} alt={preview.name} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${index}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {photoFiles.length < 10 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-[#8B4513] flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-[#8B4513] transition-colors"
                    data-testid="add-photos-btn"
                  >
                    <ImagePlus className="w-8 h-8" />
                    <span className="text-xs">Add Photos</span>
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
                data-testid="photo-file-input"
              />
            </div>

            {/* Online Presence */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Online Presence</h2>
              <p className="text-sm text-gray-600">All fields optional — add whichever platforms you use</p>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input id="website" name="website" type="url" placeholder="https://" value={formData.website} onChange={handleChange} data-testid="website-input" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input id="instagram" name="instagram" placeholder="@username" value={formData.instagram} onChange={handleChange} data-testid="instagram-input" />
                </div>
                <div>
                  <Label htmlFor="facebook">Facebook</Label>
                  <Input id="facebook" name="facebook" placeholder="facebook.com/username" value={formData.facebook} onChange={handleChange} data-testid="facebook-input" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="twitter">Twitter/X</Label>
                  <Input id="twitter" name="twitter" placeholder="@username" value={formData.twitter} onChange={handleChange} data-testid="twitter-input" />
                </div>
                <div>
                  <Label htmlFor="linkedin">LinkedIn</Label>
                  <Input id="linkedin" name="linkedin" placeholder="linkedin.com/in/username" value={formData.linkedin} onChange={handleChange} data-testid="linkedin-input" />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Additional Information</h2>
              <div>
                <Label htmlFor="other_info">Tell us more about your practice</Label>
                <Textarea
                  id="other_info"
                  name="other_info"
                  placeholder="Share your experience, certifications, specialties, and why you'd like to join Ourmend..."
                  value={formData.other_info}
                  onChange={handleChange}
                  rows={6}
                  data-testid="other-info-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#8B4513] hover:bg-[#6B3410] h-14 text-lg"
              data-testid="submit-application-btn"
            >
              {uploading ? 'Uploading photos...' : submitting ? 'Submitting...' : 'Submit Application'}
            </Button>

            <p className="text-sm text-gray-600 text-center">
              Fields marked with * are required. We'll review your application and get back to you within 3-5 business days.
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default JoinAsHealer;
